import { UserGroup, UserGroupApiError, UserGroupParams } from '@admin/api/userGroups';
import {
  useCreateUserGroupMutation,
  useDeleteUserGroupMutation,
  useUpdateUserGroupMutation,
  useUserGroupsQuery
} from '@admin/hooks/useUserGroups';
import ConfirmationDialog from '@common/components/ConfirmationDialog';
import ApiService from '@common/services/ApiService';
import EditIcon from '@common/icons/EditIcon';
import TrashIcon from '@common/icons/TrashIcon';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import UserGroupModal from './modals/UserGroupModal';

const api = new ApiService();

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// Shows the backend's message, e.g. that a group with users can't be deleted.
const notifyError = (error: Error) =>
  api.notifyError(error instanceof UserGroupApiError ? error.message : DEFAULT_ERROR_MESSAGE);

const UserGroups = () => {
  const theme = useTheme();

  // Matches the unsorted column headers rendered by AdminTable.
  const headerCellSx = {
    color: theme.typography.color.primary,
    fontSize: theme.typography.pxToRem(12),
    opacity: 0.55
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<UserGroup | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const { data: userGroups = [], isLoading } = useUserGroupsQuery();
  const createMutation = useCreateUserGroupMutation();
  const updateMutation = useUpdateUserGroupMutation();
  const deleteMutation = useDeleteUserGroupMutation();

  const handleOpenAdd = () => {
    setEditingGroup(null);
    setFieldErrors({});
    setModalOpen(true);
  };

  const handleOpenEdit = (userGroup: UserGroup) => {
    setEditingGroup(userGroup);
    setFieldErrors({});
    setModalOpen(true);
  };

  // editingGroup is left as-is so the title doesn't flip to "Add" while the
  // dialog animates out; both open handlers set it fresh.
  const handleCloseModal = () => setModalOpen(false);

  const handleSave = (params: UserGroupParams) => {
    const options = {
      onSuccess: handleCloseModal,
      onError: (error: Error) => {
        // Validation errors are shown against their fields in the modal.
        if (error instanceof UserGroupApiError && Object.keys(error.fieldErrors).length > 0) {
          setFieldErrors(error.fieldErrors);
        } else {
          notifyError(error);
        }
      }
    };
    setFieldErrors({});
    if (editingGroup) {
      updateMutation.mutate({ id: editingGroup.id, params }, options);
    } else {
      createMutation.mutate(params, options);
    }
  };

  const handleConfirmDelete = () => {
    if (!groupToDelete) {
      return;
    }
    deleteMutation.mutate(groupToDelete.id, {
      onError: notifyError,
      onSettled: () => setGroupToDelete(null)
    });
  };

  return (
    <Paper>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: `${theme.typography.pxToRem(22)} ${theme.typography.pxToRem(25)} ${theme.typography.pxToRem(25)}`
        }}
      >
        <Typography variant="h6">User Groups</Typography>
        <Button color="secondary" onClick={handleOpenAdd} size="small" variant="contained">
          <Typography variant="button">Add</Typography>
        </Button>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellSx, width: '20%' }}>Name</TableCell>
            <TableCell sx={headerCellSx}>Description</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={3} align="center">
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : userGroups.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant="body2">No user groups yet.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            userGroups.map((userGroup) => (
              <TableRow key={userGroup.id} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {userGroup.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{userGroup.description || '--'}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: '1%' }}>
                  <IconButton onClick={() => handleOpenEdit(userGroup)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => setGroupToDelete(userGroup)}>
                    <TrashIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <UserGroupModal
        open={modalOpen}
        userGroup={editingGroup}
        onClose={handleCloseModal}
        onSave={handleSave}
        isPending={createMutation.isPending || updateMutation.isPending}
        fieldErrors={fieldErrors}
      />
      <ConfirmationDialog
        open={!!groupToDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete "${groupToDelete?.name}"? This action cannot be undone.`}
        onOk={handleConfirmDelete}
        onCancel={() => setGroupToDelete(null)}
      />
    </Paper>
  );
};

export default UserGroups;
